import { useRouter } from 'next/router';
import { useReduxState } from 'hooks';
import { Button, LinkButton } from 'components/common';
import { Icon } from 'components/common/icons';

import type { CalendarEventExtended } from 'shared-types';
import type { ButtonSize } from 'components/common/Button';

interface Props {
  event: CalendarEventExtended;
  size?: ButtonSize;
}

const CalendarEventQuickNavigation = ({ event, size = 'medium' }: Props) => {
  const {
    messages,
    serverConfig: { featureFlags },
    user,
    theme
  } = useReduxState();

  const router = useRouter();

  const userIsOwnerOrSuperadmin =
    event?.userIsOwner || user?.roles.includes('superadmin');

  return (
    <div className="navigation-container">
      {userIsOwnerOrSuperadmin ? (
        <div className="quick-links">
          <div>
            <LinkButton
              category="lighter"
              href={`/calendar/${event.id}/settings`}
              icon="cog"
              iconStyle={{ fontSize: '1.2rem' }}
              isSemiRounded={true}
              size={size}
              tooltip={messages.commonSettings}
            />
          </div>
          <div>
            <LinkButton
              category="lighter"
              href={`/calendar/${event.id}/participant-settings`}
              icon="user-cog"
              iconStyle={{ fontSize: '1.2rem' }}
              isSemiRounded={true}
              size={size}
              tooltip={messages.eventCalendarParticipantsSettings}
            />
          </div>
          <div>
            <LinkButton
              category="lighter"
              href={`/calendar/${event.id}/access`}
              icon="eye"
              iconStyle={{ fontSize: '1.2rem' }}
              isSemiRounded={true}
              size={size}
              tooltip={messages.eventCalendarSettingsAccess}
            />
          </div>
          <div className="button-with-indicator">
            <LinkButton
              category="lighter"
              href={`/calendar/${event.id}/participants`}
              icon="users"
              iconStyle={{ fontSize: '1.2rem' }}
              isSemiRounded={true}
              size={size}
              tooltip={
                event.numberOfPendingAccessRequests
                  ? `${messages.commonParticipants} - ${messages.courseAccessRequest}`
                  : messages.commonParticipants
              }
            />
            {event.numberOfPendingAccessRequests > 0 ? (
              <div className="unread-items">
                {event.numberOfPendingAccessRequests}
              </div>
            ) : null}
          </div>
          {featureFlags.enableFeedbackForCalendarEvents ? (
            <div>
              <LinkButton
                category="lighter"
                href={`/calendar/${event.id}/feedback`}
                icon="bullhorn"
                iconStyle={{ fontSize: '1.2rem' }}
                isSemiRounded={true}
                size={size}
                tooltip={messages.commonFeedback}
              />
            </div>
          ) : null}
          <div>
            <LinkButton
              category="lighter"
              href={`/calendar/${event.id}/events`}
              icon="list"
              iconStyle={{ fontSize: '1.2rem' }}
              isSemiRounded={true}
              size={size}
              tooltip={messages.commonActivityLog}
            />
          </div>
          <div>
            <LinkButton
              category="lighter"
              href={`/calendar/${event.id}/admin`}
              icon="user-shield"
              iconStyle={{ fontSize: '1.2rem' }}
              isSemiRounded={true}
              size={size}
              tooltip={messages.pagesManage}
            />
          </div>
        </div>
      ) : null}

      {userIsOwnerOrSuperadmin ||
      (event.userStatus && event.userStatus !== 'queued') ? (
        <Button
          isRounded={true}
          onClick={() => router.push(`/calendar/${event.id}`)}
          size={size}
          style={{
            marginLeft: 0,
            paddingLeft: 12,
            paddingRight: 8
            //padding: '6px 8px 6px 12px'
          }}
          text={
            <span>
              {messages.eventCalendarEventPage}

              <Icon
                fixedWidth={true}
                icon="chevron-right"
                style={{ fontSize: '1rem', marginLeft: 4 }}
              />
            </span>
          }
        />
      ) : null}

      <style jsx>{`
        .navigation-container {
          display: flex;
          flex-wrap: wrap;
          gap: 10px;
        }
        .quick-links {
          display: flex;
          gap: 10px;
        }
        .button-with-indicator {
          position: relative;
        }
        .unread-items {
          background-color: ${theme.dangerColor};
          border-radius: 10px;
          color: ${theme.dangerTextColor};
          font-size: 1rem;
          font-weight: 700;
          line-height: 1;
          padding: 2px 5px;
          position: absolute;
          right: -3px;
          top: -5px;
        }
      `}</style>
    </div>
  );
};

export default CalendarEventQuickNavigation;
